/**
 * labelStyles.ts
 * -------------------------------------------------------
 * 职责：生成 echarts / echarts-gl 兼容的「双路径」标签样式。
 *
 * 背景：echarts 5.x 从 label 顶层读样式属性，
 *       echarts-gl 可能从 label.textStyle 读。
 *       所以需要同时输出扁平属性 + textStyle 嵌套。
 * -------------------------------------------------------
 */

import {
    CHINA_MAP_LABEL_FONT_SIZE,
    PROVINCE_MAP_LABEL_FONT_SIZE,
} from '../mapConstants';

/* -------- 类型 -------- */

/** makeLabelStyle 接收的参数 */
export interface LabelStyleInput {
    color: string;
    fontSize: number;
    fontWeight: number | string;
    /** 标签整体不透明度，0–1 */
    opacity?: number;
    backgroundColor?: string;
    padding?: [number, number];
    borderRadius?: number;
    borderColor?: string;
    borderWidth?: number;
    textShadowColor?: string;
    textShadowBlur?: number;
    textShadowOffsetY?: number;
}

/** makeLabelStyle 的返回：扁平属性 + textStyle 嵌套 */
export type DualPathLabel = LabelStyleInput & {
    fontFamily: string;
    textStyle: LabelStyleInput & { fontFamily: string };
};

/* -------- 工厂函数 -------- */

const FONT_FAMILY = 'Microsoft YaHei, PingFang SC, sans-serif';

/**
 * 生成双路径标签样式。
 * - 扁平属性 → echarts 5.x 读取路径
 * - textStyle 嵌套 → echarts-gl 旧读取路径
 */
export function makeLabelStyle(input: LabelStyleInput): DualPathLabel {
    const shared = { ...input, fontFamily: FONT_FAMILY };
    return { ...shared, textStyle: { ...shared } };
}

/* -------- 预设常量（用工厂一次性生成，比手写 6 遍清爽得多） -------- */

/** 全国 / 省份两套场景 × 常态 / hover / 高亮 三种状态 → 共 5 个预设 */

// ---- 全国 ----
export const CHINA_NORMAL_LABEL = makeLabelStyle({
    color: 'rgb(42, 118, 225)',
    fontSize: CHINA_MAP_LABEL_FONT_SIZE,
    fontWeight: 800,
    opacity: 0.6,
    backgroundColor: 'rgba(6, 28, 58, 0)',
    padding: [5, 10],
    borderRadius: 4,
    borderColor: 'rgba(26, 209, 232, 0)',
    borderWidth: 1,
    textShadowColor: 'rgba(0, 8, 20, 0)',
    textShadowBlur: 4,
    textShadowOffsetY: 1,
});

export const CHINA_EMPHASIS_LABEL = makeLabelStyle({
    color: '#ffffff',
    fontSize: CHINA_MAP_LABEL_FONT_SIZE,
    fontWeight: 800,
    opacity: 0.6,
    backgroundColor: 'rgba(12, 55, 98, 0)',
    padding: [5, 10],
    borderRadius: 4,
    borderColor: 'rgba(26, 209, 232, 0)',
    borderWidth: 1,
    textShadowColor: 'rgba(0, 0, 0, 0)',
    textShadowBlur: 4,
    textShadowOffsetY: 1,
});

export const CHINA_HIGHLIGHT_LABEL = makeLabelStyle({
    color: '#ffffff',
    fontSize: CHINA_MAP_LABEL_FONT_SIZE + 1,
    fontWeight: 800,
    opacity: 0.6,
    backgroundColor: 'rgba(8, 45, 88, 0)',
    padding: [5, 10],
    borderRadius: 5,
    borderColor: 'rgba(93, 240, 255, 0)',
    borderWidth: 1,
    textShadowColor: 'rgba(0, 0, 0, 0)',
    textShadowBlur: 3,
    textShadowOffsetY: 1,
});

// ---- 省份 ----
export const PROVINCE_NORMAL_LABEL = makeLabelStyle({
    color: 'rgb(149, 188, 243)',
    fontSize: PROVINCE_MAP_LABEL_FONT_SIZE,
    fontWeight: 900,
    opacity: 0.6,
    backgroundColor: 'rgba(6, 28, 58, 0)',
    padding: [4, 8],
    borderRadius: 4,
    borderColor: 'rgba(26, 209, 232, 0)',
    borderWidth: 1,
    textShadowColor: 'rgba(93, 240, 255, 0)',
    textShadowBlur: 6,
    textShadowOffsetY: 0,
});

export const PROVINCE_EMPHASIS_LABEL = makeLabelStyle({
    color: '#ffffff',
    fontSize: PROVINCE_MAP_LABEL_FONT_SIZE,
    fontWeight: 900,
    opacity: 0.6,
    backgroundColor: 'rgba(12, 55, 98, 0)',
    padding: [4, 8],
    borderRadius: 4,
    borderColor: 'rgba(26, 209, 232, 0)',
    borderWidth: 1,
    textShadowColor: 'rgba(93, 240, 255, 0)',
    textShadowBlur: 8,
    textShadowOffsetY: 0,
});
